import { createFeatureSelector, createSelector } from '@ngrx/store';

import { DevicesRouterStoreSelectors } from '../router';

import * as fromReducer from './apple-device-profile.reducer';
import { AppleDeviceProfileState, createProfileId } from './apple-device-profile.state';
import { AppleDeviceProfileEntry, AppleDeviceProfileType, ProfileDataType } from './apple-device-profile.types';

// ============================================================================
// BASE SELECTORS
// ============================================================================

export const selectAppleDeviceProfileState = createFeatureSelector<AppleDeviceProfileState>(
  fromReducer.APPLE_DEVICE_PROFILE_FEATURE_KEY
);

export const selectAllAppleDeviceProfiles = createSelector(
  selectAppleDeviceProfileState,
  fromReducer.selectAppleDeviceProfiles
);

export const selectAppleDeviceProfileEntities = createSelector(
  selectAppleDeviceProfileState,
  fromReducer.selectAppleDeviceProfileEntities
);

export const selectAppleDeviceProfileIds = createSelector(
  selectAppleDeviceProfileState,
  fromReducer.selectAppleDeviceProfileIds
);

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard to check if an entry matches the expected profile type.
 * This enables TypeScript to narrow the type without type casts.
 */
function isProfileEntryType<T extends AppleDeviceProfileType>(
  profileType: T,
  entry: AppleDeviceProfileEntry
): entry is AppleDeviceProfileEntry<T> {
  return entry.profileType === profileType;
}

// ============================================================================
// PARAMETERIZED SELECTORS
// ============================================================================

/**
 * Select a specific item by UDID, profile type, and item ID.
 * Use for selecting a specific certificate, configuration profile, etc.
 *
 * @example
 * ```typescript
 * this.store.select(selectProfileItem('device-01', 'certificate', 'Cert1'))
 * // Returns Observable<Certificate | undefined>
 * ```
 */
export function selectProfileItem<T extends AppleDeviceProfileType>(udid: string, profileType: T, itemId: string) {
  return createSelector(selectAppleDeviceProfileEntities, (entities): ProfileDataType<T> | undefined => {
    const entry = entities[createProfileId(udid, profileType, itemId)];
    if (!entry || !isProfileEntryType(profileType, entry)) {
      return undefined;
    }
    return entry.data;
  });
}

// ============================================================================
// ROUTER-AWARE SELECTORS (use UDID from route)
// ============================================================================

/**
 * Select a single-item profile using UDID from route.
 * Use for profiles like device-information, security-info.
 *
 * @example
 * ```typescript
 * this.store.select(selectProfileByType('security-info'))
 * // Returns Observable<SecurityInfo | undefined>
 * ```
 */
export function selectProfileByType<T extends AppleDeviceProfileType>(profileType: T) {
  return createSelector(
    selectAllAppleDeviceProfiles,
    DevicesRouterStoreSelectors.selectUdid,
    (profiles, udid): ProfileDataType<T> | undefined => {
      const entry = profiles.find((p) => p.udid === udid && p.profileType === profileType && !p.itemId);
      if (!entry || !isProfileEntryType(profileType, entry)) {
        return undefined;
      }
      return entry.data;
    }
  );
}

/**
 * Select all items for a multi-item profile using UDID from route.
 * Use for profiles like certificate, configuration-profile.
 *
 * @example
 * ```typescript
 * this.store.select(selectProfileItemsByType('certificate'))
 * // Returns Observable<Certificate[]>
 * ```
 */
export function selectProfileItemsByType<T extends AppleDeviceProfileType>(profileType: T) {
  return createSelector(
    selectAllAppleDeviceProfiles,
    DevicesRouterStoreSelectors.selectUdid,
    (profiles, udid): ProfileDataType<T>[] =>
      profiles
        .filter((p): p is AppleDeviceProfileEntry<T> => p.udid === udid && isProfileEntryType(profileType, p))
        .map((p) => p.data)
  );
}

/**
 * Select a specific item by profile type and item ID using UDID from route.
 * Use for selecting a specific certificate, configuration profile, etc.
 *
 * @example
 * ```typescript
 * this.store.select(selectProfileItemByType('certificate', 'Cert1'))
 * // Returns Observable<Certificate | undefined>
 * ```
 */
export function selectProfileItemByType<T extends AppleDeviceProfileType>(profileType: T, itemId: string) {
  return createSelector(
    selectAppleDeviceProfileEntities,
    DevicesRouterStoreSelectors.selectUdid,
    (entities, udid): ProfileDataType<T> | undefined => {
      if (!udid) {
        return undefined;
      }

      const entry = entities[createProfileId(udid, profileType, itemId)];
      if (!entry || !isProfileEntryType(profileType, entry)) {
        return undefined;
      }
      return entry.data;
    }
  );
}
